/**
 * <s-carousel> — a horizontally-scrollable "swim lane" that shows multiple items at once.
 *
 * The track is a native scroll-snap container, so touch swiping, trackpads and keyboard
 * scrolling work without JS. This component enhances it with:
 *   - prev/next buttons that glide one item at a time and disable at either end
 *   - mouse drag-to-scroll that settles onto the nearest item
 *   - instant (non-animated) movement when the visitor prefers reduced motion
 *
 * Expected DOM (rendered by blocks/carousel.liquid):
 *   <s-carousel>
 *     [<button data-carousel-prev> / <button data-carousel-next>]
 *     <div data-carousel-track> …items… </div>
 *   </s-carousel>
 */
class SCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector("[data-carousel-track]");
    this.prevButton = this.querySelector("[data-carousel-prev]");
    this.nextButton = this.querySelector("[data-carousel-next]");

    if (!this.track) return;

    this.abort = new AbortController();
    const { signal } = this.abort;
    this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    this.prevButton?.addEventListener("click", () => this.step(-1), { signal });
    this.nextButton?.addEventListener("click", () => this.step(1), { signal });
    this.track.addEventListener("scroll", () => this.updateControls(), { passive: true, signal });

    // Mouse drag only; touch and pen already scroll natively with momentum.
    this.track.addEventListener("pointerdown", (event) => this.onPointerDown(event), { signal });
    this.track.addEventListener("pointermove", (event) => this.onPointerMove(event), { signal });
    this.track.addEventListener("pointerup", (event) => this.onPointerUp(event), { signal });
    this.track.addEventListener("pointercancel", (event) => this.onPointerUp(event), { signal });
    this.track.addEventListener("dragstart", (event) => event.preventDefault(), { signal });
    // Swallow the click that ends a drag so linked tiles don't open by accident.
    this.track.addEventListener(
      "click",
      (event) => {
        if (this.suppressClick) {
          event.preventDefault();
          event.stopPropagation();
          this.suppressClick = false;
        }
      },
      { capture: true, signal },
    );

    this.resizeObserver = new ResizeObserver(() => this.updateControls());
    this.resizeObserver.observe(this.track);

    this.updateControls();
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.resizeObserver?.disconnect();
  }

  get isRtl() {
    return getComputedStyle(this.track).direction === "rtl";
  }

  get behavior() {
    return this.reducedMotion.matches ? "auto" : "smooth";
  }

  /**
   * Move one item forward or back, honoring reading direction.
   * @param {number} direction -1 for previous, 1 for next.
   */
  step(direction) {
    const first = this.track.firstElementChild;
    if (!first) return;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const distance = (first.getBoundingClientRect().width + gap) * direction * (this.isRtl ? -1 : 1);
    this.track.scrollBy({ left: distance, behavior: this.behavior });
  }

  onPointerDown(event) {
    if (event.pointerType !== "mouse" || event.button !== 0) return;
    this.drag = { x: event.clientX, scroll: this.track.scrollLeft, moved: false, id: event.pointerId };
  }

  onPointerMove(event) {
    if (!this.drag || event.pointerId !== this.drag.id) return;
    const dx = event.clientX - this.drag.x;
    if (!this.drag.moved) {
      if (Math.abs(dx) < 4) return;
      this.drag.moved = true;
      this.track.setPointerCapture(event.pointerId);
      // Snap and smooth scrolling fight a hand-driven scroll, so pause them while dragging.
      this.track.style.scrollSnapType = "none";
      this.track.style.scrollBehavior = "auto";
      this.track.style.cursor = "grabbing";
    }
    this.track.scrollLeft = this.drag.scroll - dx;
  }

  onPointerUp(event) {
    if (!this.drag || event.pointerId !== this.drag.id) return;
    const { moved } = this.drag;
    this.drag = null;
    if (!moved) return;

    this.suppressClick = true;
    if (this.track.hasPointerCapture(event.pointerId)) this.track.releasePointerCapture(event.pointerId);
    this.track.style.cursor = "";
    this.track.style.scrollBehavior = "";
    this.settleToNearest();
  }

  /** Glide to the item closest to the snap edge, then hand control back to CSS snapping. */
  settleToNearest() {
    const rtl = this.isRtl;
    const trackRect = this.track.getBoundingClientRect();
    const styles = getComputedStyle(this.track);
    const edge = rtl
      ? trackRect.right - (parseFloat(styles.scrollPaddingInlineStart) || 0)
      : trackRect.left + (parseFloat(styles.scrollPaddingInlineStart) || 0);

    let delta = 0;
    let best = Infinity;
    for (const item of this.track.children) {
      const rect = item.getBoundingClientRect();
      const offset = (rtl ? rect.right : rect.left) - edge;
      if (Math.abs(offset) < best) {
        best = Math.abs(offset);
        delta = offset;
      }
    }

    const restoreSnap = () => {
      this.track.style.scrollSnapType = "";
    };
    if (Math.abs(delta) < 1) {
      restoreSnap();
      return;
    }
    this.track.scrollBy({ left: delta, behavior: this.behavior });
    if ("onscrollend" in this.track && this.behavior === "smooth") {
      this.track.addEventListener("scrollend", restoreSnap, { once: true });
    } else {
      setTimeout(restoreSnap, this.behavior === "smooth" ? 450 : 0);
    }
  }

  /** Disable prev/next when the track is at its respective end. */
  updateControls() {
    if (!this.prevButton && !this.nextButton) return;

    // scrollLeft runs negative in RTL; compare magnitudes so both directions work.
    const position = Math.abs(this.track.scrollLeft);
    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    const atStart = position <= 1;
    const atEnd = position >= maxScroll - 1;
    const overflowing = maxScroll > 1;

    if (this.prevButton) this.prevButton.disabled = !overflowing || atStart;
    if (this.nextButton) this.nextButton.disabled = !overflowing || atEnd;
  }
}

if (!customElements.get("s-carousel")) {
  customElements.define("s-carousel", SCarousel);
}
